import React from 'react';
import {BrowseExpertsFilters} from 'sections/types';
import {Stack, Tag, Button} from '@shopify/polaris';
import {WithI18nProps, withI18n} from '@shopify/react-i18n';
import {formatCurrency} from 'utilities/numbers';
import {Profile} from 'sections/services-marketplace/experts/utilities';

import styles from './FilterCollection.scss';

interface OwnProps {
  filterState: BrowseExpertsFilters;
  filterChangeHandlers: {
    onPriceRangeReset(): void;
    onLocationRemove(): void;
    onLanguageRemove(value: string): () => void;
    onResetFilters(): void;
  };
}

type CombinedProps = OwnProps & WithI18nProps;

function FilterCollection({
  filterState: {minPrice, maxPrice, languages, location},
  filterChangeHandlers: {
    onPriceRangeReset,
    onLanguageRemove,
    onLocationRemove,
    onResetFilters,
  },
  i18n,
}: CombinedProps) {
  function hasActiveFilters() {
    return minPrice || maxPrice || (languages && languages.length) || location;
  }

  return (
    <div className={styles.FilterCollection}>
      <Stack alignment="center">
        {(minPrice || maxPrice) && (
          <PriceRangeTag
            minPrice={minPrice}
            maxPrice={maxPrice}
            onRemove={onPriceRangeReset}
            i18n={i18n}
          />
        )}

        {location && (
          <LocationTag location={location} onRemove={onLocationRemove} />
        )}

        {languages &&
          languages.map(language => (
            <LanguageTag
              key={language}
              language={language}
              onRemove={onLanguageRemove}
            />
          ))}

        {hasActiveFilters() && (
          <Stack.Item>
            <Button plain size="slim" onClick={onResetFilters}>
              {i18n.translate('ExpertIndex.EmptyState.buttonLabel')}
            </Button>
          </Stack.Item>
        )}
      </Stack>
    </div>
  );
}

function PriceRangeTag({
  minPrice,
  maxPrice,
  onRemove,
  i18n,
}: {
  minPrice?: string;
  maxPrice?: string;
  onRemove(): void;
} & WithI18nProps) {
  const minPriceText = minPrice
    ? formatCurrency(minPrice)
    : i18n.translate('ExpertIndex.Filters.noMinLabel');

  const maxPriceText = maxPrice
    ? formatCurrency(maxPrice)
    : i18n.translate('ExpertIndex.Filters.noMaxLabel');

  return <Tag onRemove={onRemove}>{`${minPriceText} - ${maxPriceText}`}</Tag>;
}

function LanguageTag({
  language,
  onRemove,
}: {
  language: string;
  onRemove(language: string): () => void;
}) {
  return (
    <Tag onRemove={onRemove(language)}>
      {Profile.getLanguageLabel(language)}
    </Tag>
  );
}

function LocationTag({
  location,
  onRemove,
}: {
  location?: string;
  onRemove(): void;
}) {
  return <Tag onRemove={onRemove}>{location}</Tag>;
}

export default withI18n()(FilterCollection);
